// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Modal, Nav, Form, Col, Tab, Row } from "react-bootstrap";

import { ActionType, IAction, IActionTypeChangeReports, raiseAction } from "actions";
import { DataState } from "types";
import { UserRole } from "graphql/api";
import {
  ChargerColum,
  ChargerReportContent,
  DeviceType,
  ExtendedPeriod,
  IChargerReportFilter,
  IoReportContent,
  Period,
  Report,
  SiteReportContent,
  TrendingColum,
  TrendReportContent,
} from "dbtypes";
import { stringEnumToLabels } from "guiutils";
import { ITariffCircuit } from "parameter";
import { getLocalDate, isSet } from "utils";
import { IState } from "states/states";
import { ModalPanel } from "states/guistates";
import { User } from "states/sitestates";

import SaveFeedback from "view/comp/SaveFeedback";
import { IRfid } from "states/ocppstates";

type IModalReportSettingsItemProps = {
  isOpen: boolean;
  curUser: User | undefined;
  isVirtualSite: boolean;
  siteId: number | undefined;
  reportId: number | undefined; // undefined for new reports
  dataState: DataState;
  oldValue: Report | undefined;
  tariffCircuits: ITariffCircuit[];
  rfids: IRfid[];
};

const mapStateToProps = (state: IState) => {
  const isOpen = state.gui.modal.panel === ModalPanel.REPORT_SETTINGS;
  const siteId = state.cached.selectedSite;
  const site = state.site.siteList?.find((curSite) => curSite.id === siteId);
  if (site === undefined) {
    console.error("admin unidentified report site", siteId, state.site.siteList);
  }
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const oldValue = site?.cloudSettings?.reports?.[state.gui.modalReport!];
  const props: IModalReportSettingsItemProps = {
    isOpen,
    curUser,
    isVirtualSite: site?.deviceType === DeviceType.VIRTUAL,
    siteId,
    reportId: state.gui.modalReport,
    dataState: state.scheduler.saveState,
    oldValue,
    tariffCircuits: state.parameter?.tariffline ?? [],
    rfids: state.ocpp.rfid,
  };
  return props;
};

type IModalReportSettingsState = Report & {
  wasOpen: boolean;
  isChanged: boolean;
};

interface IModalReportSettingsItemDispatch {
  onCancelClick: () => void;
  onSaveClick: (
    siteId: number,
    reportId: number | undefined,
    state: IModalReportSettingsState,
  ) => void;
  onDeleteClick: (siteId: number, reportId: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalReportSettingsItemDispatch = {
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
    onSaveClick: (
      siteId: number,
      reportId: number | undefined,
      state: IModalReportSettingsState,
    ) => {
      const { wasOpen, isChanged, ...report } = state;
      const payload: IActionTypeChangeReports = {
        siteId,
        reportId,
        report,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_REPORT_REQ, payload));
    },
    onDeleteClick: (siteId: number, reportId: number) => {
      const payload: IActionTypeChangeReports = {
        siteId,
        reportId,
        report: undefined,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_REPORT_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  titleDefault: {
    id: "new report",
  },
  titleHint: {
    id: "custom name of report",
  },
  selectHint: {
    id: "press CTRL (CMD on Mac) key to select mutliple items",
  },
  exportTargetHint: {
    id: "email address for report receipt",
  },
  groupByNone: {
    id: "none",
  },
  groupByDate: {
    id: "date",
  },
  groupByCircuit: {
    id: "tariff circuit",
  },
  groupByThen: {
    id: "then",
  },
  groupByCharger: {
    id: "charger",
  },
  groupByRfid: {
    id: "charging card",
  },
});

class ModalReportSettingsItemComponent extends React.Component<
  IModalReportSettingsItemProps & IModalReportSettingsItemDispatch & WrappedComponentProps,
  IModalReportSettingsState
> {
  state: IModalReportSettingsState = {
    wasOpen: false,
    isChanged: false,
    ...this.makeEmptyReport(this.props.isVirtualSite ? "charger" : "trend"),
  };

  componentDidUpdate() {
    // check for changes in old value
    if (this.props.isOpen !== this.state.wasOpen) {
      if (isSet(this.props.reportId)) {
        // load new report data to local state
        this.setState({
          wasOpen: this.props.isOpen,
          isChanged: false,
          ...this.props.oldValue!,
        });
      } else {
        // load new empty report
        this.setState({
          wasOpen: this.props.isOpen,
          isChanged: false,
          ...this.makeEmptyReport(this.props.isVirtualSite ? "charger" : "trend"),
        });
      }
    }
  }

  public render() {
    if (!this.props.isOpen) return <></>; // avoid rendering of inexisting data

    return (
      <Modal show={this.props.isOpen} onHide={this.props.onCancelClick}>
        <Modal.Header closeButton>
          <FormattedMessage id="Report Settings" />
        </Modal.Header>
        <Modal.Body>{this.renderSettings()}</Modal.Body>
        <Modal.Footer>
          <SaveFeedback dataState={this.props.dataState} />
          <Button
            id="cancelReportSettings"
            variant="secondary"
            className="me-1"
            onClick={this.props.onCancelClick}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          {(this.state.isChanged || this.props.reportId === undefined) && (
            <Button
              id="saveReportSettings"
              variant="secondary"
              className="me-1"
              disabled={!this.isSettingsOk()}
              onClick={(e) =>
                this.props.onSaveClick(this.props.siteId!, this.props.reportId, this.state)
              }
            >
              <FormattedMessage id="Save" />
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }

  private isSettingsOk() {
    // inhibit set auto-export without email
    if (this.state.autoExport.interval !== "none" && !this.state.autoExport.target) {
      return false;
    }
    return true;
  }

  private renderSettings() {
    return (
      <>
        <h1>
          {this.state.type === "trend" && <FormattedMessage id="Trend Report" />}
          {this.state.type === "charger" && <FormattedMessage id="Charging Report" />}
          {this.state.type === "io" && <FormattedMessage id="Input/Output Report" />}
          {this.state.type === "site" && <FormattedMessage id="Site Report" />}
        </h1>
        <Form.Group as={Row} className="mb-2">
          <Form.Label htmlFor="title" sm={3}>
            <FormattedMessage id="Title" />
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              id="title"
              placeholder={this.props.intl.formatMessage(messages.titleHint)}
              type="text"
              value={this.state.title}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  title: event?.currentTarget.value,
                });
              }}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Col sm={9}>
            <Form.Check
              id="isActive"
              className="mt-2"
              checked={this.state.isActive}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  isActive: event?.currentTarget.checked,
                });
              }}
              label={<FormattedMessage id="Activated" />}
            />
            {!this.state.isActive && isSet(this.props.reportId) && (
              <Button
                style={{ marginTop: -2 }}
                id="deleteReport"
                variant="danger"
                className="ms-3"
                onClick={(_event) =>
                  this.props.onDeleteClick(this.props.siteId!, this.props.reportId!)
                }
              >
                <FormattedMessage id="Delete Report" />
              </Button>
            )}
          </Col>
        </Form.Group>
        <div>
          <Nav variant="tabs">
            <Nav.Item>
              <Nav.Link
                className={this.state.type === "trend" ? "active" : "inactive"}
                onClick={() => {
                  this.setState({
                    isChanged: true,
                    ...this.makeEmptyReport("trend"),
                    title: this.state.title,
                    isActive: this.state.isActive,
                    autoExport: this.state.autoExport,
                  });
                }}
              >
                <FormattedMessage id="Trend" />
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className={this.state.type === "charger" ? "active" : "inactive"}
                onClick={() => {
                  this.setState({
                    isChanged: true,
                    ...this.makeEmptyReport("charger"),
                    title: this.state.title,
                    isActive: this.state.isActive,
                    autoExport: this.state.autoExport,
                  });
                }}
              >
                <FormattedMessage id="Charging" />
              </Nav.Link>
            </Nav.Item>
            {!this.props.isVirtualSite && (
              <Nav.Item>
                <Nav.Link
                  className={this.state.type === "io" ? "active" : "inactive"}
                  onClick={() => {
                    this.setState({
                      isChanged: true,
                      ...this.makeEmptyReport("io"),
                      title: this.state.title,
                      isActive: this.state.isActive,
                      autoExport: this.state.autoExport,
                    });
                  }}
                >
                  <FormattedMessage id="IOs" />
                </Nav.Link>
              </Nav.Item>
            )}
            {[UserRole.admin, UserRole.root].includes(
              this.props.curUser?.role ?? UserRole.viewer,
            ) && (
              <Nav.Item>
                <Nav.Link
                  className={this.state.type === "site" ? "active" : "inactive"}
                  onClick={() => {
                    this.setState({
                      isChanged: true,
                      ...this.makeEmptyReport("site"),
                      title: this.state.title,
                      isActive: this.state.isActive,
                      autoExport: this.state.autoExport,
                    });
                  }}
                >
                  <FormattedMessage id="Sites" />
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
          <Tab.Content>
            {this.state.type === "trend" && this.renderTrendSettings()}
            {this.state.type === "charger" && this.renderChargerSettings()}
            {this.state.type === "io" && this.renderIoSettings()}
            {this.state.type === "site" && this.renderSiteSettings()}
          </Tab.Content>
        </div>
        {this.renderFileFormat()}
        {this.state.fileFormat === "csv" && this.renderCSVDelimiter()}
        {this.renderHideSumRowCheckbox()}
        {this.renderExport()}
      </>
    );
  }

  private renderTrendSettings() {
    const isEvuTrend = (this.state.content as TrendReportContent).isEvuFormat === true;
    const tariffIndexes = Array.from(Array(this.props.tariffCircuits.length).keys())
      .filter((curId) => this.props.tariffCircuits[curId].webActive)
      .sort((a, b) =>
        this.props.tariffCircuits[a].name.localeCompare(this.props.tariffCircuits[b].name),
      );
    const tariffItems = tariffIndexes.map((curIndex) => {
      const curTariffCircuit = this.props.tariffCircuits[curIndex];
      return (
        <option key={`${curIndex}`} value={`${curTariffCircuit.id ?? curIndex}`}>
          {curTariffCircuit.name}
        </option>
      );
    });

    return (
      <>
        <Form.Group as={Row} className="mb-3">
          <Col sm={9}>
            <Form.Check
              id="isEvuFormat"
              style={{ marginTop: 9 }}
              checked={isEvuTrend}
              onChange={(event) => {
                // ensure valid settings for EVU format
                const content = { ...this.state.content } as TrendReportContent;
                if (event.currentTarget.checked) {
                  content.columns = [TrendingColum.ENERGY];
                  content.groupBy = ["date", "circuit"];
                }
                this.setState({
                  isChanged: true,
                  content: {
                    ...content,
                    isEvuFormat: event.currentTarget.checked,
                  },
                });
              }}
              label={<FormattedMessage id="EVU Format" />}
            />
          </Col>
        </Form.Group>
        {this.renderDataColumns()}
        {this.renderGrouping()}
        {!isEvuTrend && this.renderTrendReference(tariffItems)}
        {this.renderFilter(tariffItems)}
      </>
    );
  }

  private renderDataColumns() {
    const isTrend = this.state.type === "trend";
    const isEvuTrend = isTrend && (this.state.content as TrendReportContent).isEvuFormat === true;
    const columns =
      this.state.type === "charger"
        ? stringEnumToLabels(ChargerColum)
        : isEvuTrend
        ? [TrendingColum.ENERGY, TrendingColum.MAX_ENERGY, TrendingColum.COST]
        : ["none", Period.HOUR].includes((this.state.content as TrendReportContent).aggregation)
        ? stringEnumToLabels(TrendingColum).filter(
            (curItem) =>
              ![
                TrendingColum.ENERGY_AVG,
                TrendingColum.ENERGY_MIN,
                TrendingColum.ENERGY_MAX,
              ].includes(curItem),
          )
        : stringEnumToLabels(TrendingColum);
    const columnItems = columns.map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.props.intl.formatMessage({
            id: `${isTrend ? "TrendingColumn" : "ChargerColumn"}_${curItem}`,
          })}
        </option>
      );
    });

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label htmlFor="columns" sm={3}>
          <FormattedMessage id="Data" />
        </Form.Label>
        <Col sm={9}>
          <Form.Select
            id="columns"
            multiple={!isEvuTrend}
            title={this.props.intl.formatMessage(messages.selectHint)}
            value={(this.state.content as TrendReportContent).columns}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                content: {
                  ...(this.state.content as TrendReportContent),
                  columns: Array.from(
                    (event.currentTarget as unknown as HTMLSelectElement).selectedOptions,
                  ).map((item) => item.value) as TrendingColum[],
                },
              });
            }}
          >
            {columnItems}
          </Form.Select>
        </Col>
      </Form.Group>
    );
  }

  private renderGrouping() {
    const content = this.state.content as TrendReportContent;
    const isTrend = this.state.type === "trend";
    const isEvuTrend = isTrend && (this.state.content as TrendReportContent).isEvuFormat === true;
    const groupByValues = isEvuTrend
      ? [[], ["date"], ["circuit"], ["date", "circuit"]]
      : isTrend
      ? [[], ["date"], ["circuit"], ["date", "circuit"], ["circuit", "date"]]
      : [];
    const groupByItems = groupByValues
      .map((curVal) => {
        if (curVal.length === 0) {
          return this.props.intl.formatMessage(messages.groupByNone);
        }
        return curVal
          .map((curItem) =>
            this.props.intl.formatMessage(
              curItem === "date"
                ? messages.groupByDate
                : curItem === "circuit"
                ? messages.groupByCircuit
                : curItem === "rfid"
                ? messages.groupByRfid
                : messages.groupByCharger,
            ),
          )
          .join(`, ${this.props.intl.formatMessage(messages.groupByThen)} `);
      })
      .map((curString, curIndex) => {
        return (
          <option key={curIndex} value={groupByValues[curIndex]}>
            {curString}
          </option>
        );
      });
    // aggregation is used for date grouping in trend and for IOs
    const aggregationItems = ["none", ...stringEnumToLabels(Period)].map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.props.intl.formatMessage({ id: `Period_${curItem}` })}
        </option>
      );
    });

    return (
      <>
        {groupByItems.length > 0 && (
          <Form.Group as={Row} className="mb-3">
            <Form.Label htmlFor="groupBy" sm={3}>
              <FormattedMessage id="Group By" />
            </Form.Label>
            <Col sm={9}>
              <Form.Select
                id="groupBy"
                value={content.groupBy?.join(",")}
                onChange={(event) => {
                  const groupBy = event.currentTarget.value.length
                    ? (event.currentTarget.value.split(",") as ("date" | "circuit")[])
                    : [];
                  const stateChange = {
                    isChanged: true,
                    content: {
                      ...content,
                      groupBy,
                    },
                  };
                  if (
                    content.reference?.type === "date" &&
                    !stateChange.content.groupBy?.includes("date")
                  ) {
                    stateChange.content.reference = undefined;
                  }
                  if (
                    content.reference?.type === "circuit" &&
                    !stateChange.content.groupBy?.includes("circuit")
                  ) {
                    stateChange.content.reference = undefined;
                  }
                  this.setState(stateChange);
                }}
              >
                {groupByItems}
              </Form.Select>
            </Col>
          </Form.Group>
        )}
        {
          /* this.state.type === "io" || */ content.groupBy?.includes("date") && (
            <Form.Group as={Row} className="mb-3">
              <Form.Label htmlFor="aggregation" sm={3}>
                <FormattedMessage id="Aggregation" />
              </Form.Label>
              <Col sm={9}>
                <Form.Select
                  id="aggregation"
                  value={content.aggregation}
                  onChange={(event) => {
                    this.setState({
                      isChanged: true,
                      content: {
                        ...content,
                        aggregation: event.currentTarget.value as Period | "none",
                      },
                    });
                  }}
                >
                  {aggregationItems}
                </Form.Select>
              </Col>
            </Form.Group>
          )
        }
      </>
    );
  }

  private renderTrendReference(tariffItems: JSX.Element[]) {
    const content = this.state.content as TrendReportContent;

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label sm={3}>
          <FormattedMessage id="Reference" />
        </Form.Label>
        <Col sm={9}>
          <Form.Check
            id="reference_none"
            name="reference"
            type="radio"
            className="mb-2"
            checked={content.reference === undefined}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                content: {
                  ...content,
                  reference: undefined,
                },
              });
            }}
            label={<FormattedMessage id="no reference" />}
          />
          <Form.Check
            id="reference_circuit"
            name="reference"
            type="radio"
            className="mb-2"
            disabled={!content.groupBy?.includes("circuit")}
            checked={content.reference?.type === "circuit"}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                content: {
                  ...content,
                  reference: {
                    type: "circuit",
                    ref: typeof content.reference?.ref === "number" ? content.reference.ref : 0,
                  },
                },
              });
            }}
            label={this.renderReferenceCircuitLabel(tariffItems)}
          />
          <Form.Check
            id="reference_date"
            name="reference"
            type="radio"
            disabled={!content.groupBy?.includes("date")}
            checked={content.reference?.type === "date"}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                content: {
                  ...content,
                  reference: {
                    type: "date",
                    ref: Period.DAY,
                  },
                },
              });
            }}
            label={this.renderReferenceDateLabel()}
          />
        </Col>
      </Form.Group>
    );
  }

  private renderReferenceCircuitLabel(tariffItems: JSX.Element[]) {
    const content = this.state.content as TrendReportContent;
    return (
      <>
        <FormattedMessage id="other tariff circuit" />
        {content.reference?.type === "circuit" && (
          <>
            <Form.Select
              value={content.reference.ref}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  content: {
                    ...content,
                    reference: {
                      type: "circuit",
                      ref: parseInt(event.currentTarget.value),
                    },
                  },
                });
              }}
            >
              {tariffItems}
            </Form.Select>
          </>
        )}
      </>
    );
  }

  private renderReferenceDateLabel() {
    const content = this.state.content as TrendReportContent;
    const periodRefItems = [Period.YEAR, Period.MONTH, Period.DAY].map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.props.intl.formatMessage({ id: `Period_${curItem}` })}
        </option>
      );
    });
    return (
      <>
        <FormattedMessage id="previous time period" />
        {content.reference?.type === "date" && (
          <>
            <Form.Select
              value={content.reference.ref}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  content: {
                    ...content,
                    reference: {
                      type: "date",
                      ref: event.currentTarget.value as Period,
                    },
                  },
                });
              }}
            >
              {periodRefItems}
            </Form.Select>
          </>
        )}
      </>
    );
  }

  private renderFilter(filterItems?: JSX.Element[]) {
    const isTrend = this.state.type === "trend";
    const isCharger = this.state.type === "charger";
    const content = this.state.content as TrendReportContent;

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label sm={3}>
          <FormattedMessage id="Filter" />
        </Form.Label>
        <Col sm={9}>
          {isTrend && isSet(filterItems) && (
            <Form.Check
              id="filterCircuit"
              className="mb-2"
              checked={
                content.filter.filter((curItem) =>
                  ["circuit", "input", "output"].includes(curItem.type),
                ).length > 0
              }
              onChange={(event) => {
                const filter = content.filter.filter(
                  (curItem) => !["circuit", "input", "output"].includes(curItem.type),
                );
                if (event.currentTarget.checked) {
                  filter.push({ type: isTrend ? "circuit" : ("output" as any), ref: 0 });
                }
                this.setState({ isChanged: true, content: { ...content, filter } });
              }}
              label={this.renderFilterCircuitLabel(filterItems)}
            />
          )}

          <Form.Check
            id="filterBegin"
            className="mb-2"
            checked={content.filter.filter((curItem) => curItem.type === "begin").length > 0}
            onChange={(event) => {
              const filter = content.filter.filter((curItem) => curItem.type !== "begin");
              if (event.currentTarget.checked) {
                filter.push({ type: "begin", ref: Math.round(Date.now() / 1000) });
              }
              this.setState({
                isChanged: true,
                content: { ...content, filter },
              });
            }}
            label={this.renderFilterBeginLabel()}
          />
          <Form.Check
            id="filterEnd"
            className="mb-2"
            checked={content.filter.filter((curItem) => curItem.type === "end").length > 0}
            onChange={(event) => {
              const filter = content.filter.filter((curItem) => curItem.type !== "end");
              if (event.currentTarget.checked) {
                filter.push({ type: "end", ref: Math.round(Date.now() / 1000) });
              }
              this.setState({ isChanged: true, content: { ...content, filter } });
            }}
            label={this.renderFilterEndLabel()}
          />

          <Form.Check
            id="filterPeriod"
            className="mb-2"
            checked={content.filter.filter((curItem) => curItem.type === "period").length > 0}
            onChange={(event) => {
              const filter = content.filter.filter((curItem) => curItem.type !== "period");
              if (event.currentTarget.checked) {
                filter.push({ type: "period", ref: Period.MONTH });
              }
              this.setState({ isChanged: true, content: { ...content, filter } });
            }}
            label={this.renderFilterPeriodLabel()}
          />

          {isTrend && (
            <Form.Check
              id="hideEmpty"
              className="mb-2"
              checked={content.hideEmpty}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  content: { ...content, hideEmpty: event?.currentTarget.checked },
                });
              }}
              label={<FormattedMessage id="hide zero data values" />}
            />
          )}

          {isCharger && this.renderChargerFilters(filterItems)}
        </Col>
      </Form.Group>
    );
  }

  private renderFilterCircuitLabel(filterItems?: JSX.Element[]) {
    const content = this.state.content as TrendReportContent;
    return (
      <>
        <FormattedMessage id="tariff circuits" />
        {content.filter.filter((curItem) => curItem.type === "circuit").length > 0 && (
          <>
            <Form.Select
              className="mb-2"
              multiple={true}
              title={this.props.intl.formatMessage(messages.selectHint)}
              value={content.filter
                .filter((curItem) => curItem.type === "circuit")
                .map((curItem) => `${curItem.ref}`)}
              onChange={(event) => {
                const filter = content.filter.filter((curItem) => curItem.type !== "circuit");
                const tlList = Array.from(
                  (event.currentTarget as unknown as HTMLSelectElement).selectedOptions,
                ).map((item) => parseInt(item.value));
                tlList.forEach((curTl) => {
                  filter.push({ type: "circuit", ref: curTl });
                });
                this.setState({ isChanged: true, content: { ...content, filter } });
              }}
            >
              {filterItems}
            </Form.Select>
          </>
        )}
      </>
    );
  }

  private renderFilterBeginLabel() {
    const content = this.state.content as TrendReportContent;
    return (
      <>
        <FormattedMessage id="start date" />
        {content.filter.filter((curItem) => curItem.type === "begin").length > 0 && (
          <>
            :{" "}
            <input
              type="date"
              className="mb-2"
              style={{ display: "inline-block", marginTop: -5 }}
              value={getLocalDate(
                (content.filter.find((curItem) => curItem.type === "begin")!.ref as number) * 1000,
              )}
              onChange={(event) => {
                const newDate = event.currentTarget.valueAsDate;
                const oldFilter = content.filter.find((curItem) => curItem.type === "begin")!;
                const newFilter = content.filter.filter((curItem) => curItem.type !== "begin");
                if (newDate) {
                  newFilter.push({
                    ...oldFilter,
                    ref: event.currentTarget.valueAsDate!.getTime() / 1000,
                  });
                }
                this.setState({ isChanged: true, content: { ...content, filter: newFilter } });
              }}
            />
          </>
        )}
      </>
    );
  }

  private renderFilterEndLabel() {
    const content = this.state.content as ChargerReportContent;
    return (
      <>
        <FormattedMessage id="end date" />
        {content.filter.filter((curItem) => curItem.type === "end").length > 0 && (
          <>
            :{" "}
            <input
              type="date"
              className="mb-2"
              style={{ display: "inline-block", marginTop: -5 }}
              value={getLocalDate(
                (content.filter.find((curItem) => curItem.type === "end")!.ref as number) * 1000,
              )}
              onChange={(event) => {
                const newDate = event.currentTarget.valueAsDate;
                const oldFilter = content.filter.find((curItem) => curItem.type === "end")!;
                const newFilter = content.filter.filter((curItem) => curItem.type !== "end");
                if (newDate) {
                  newFilter.push({
                    ...oldFilter,
                    ref: event.currentTarget.valueAsDate!.getTime() / 1000,
                  });
                }
                this.setState({ isChanged: true, content: { ...content, filter: newFilter } });
              }}
            />
          </>
        )}
      </>
    );
  }

  private renderFilterPeriodLabel() {
    const content = this.state.content as TrendReportContent;
    const periodItems = [
      ExtendedPeriod.LAST_DAY,
      Period.DAY,
      ExtendedPeriod.LAST_MONTH,
      Period.MONTH,
      ExtendedPeriod.LAST_YEAR,
      Period.YEAR,
    ].map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.props.intl.formatMessage({ id: `FilterPeriod_${curItem}` })}
        </option>
      );
    });

    return (
      <>
        <FormattedMessage id="period" />
        {content.filter.filter((curItem) => curItem.type === "period").length > 0 && (
          <>
            :{" "}
            <Form.Select
              className="mb-2"
              style={{ display: "inline-block" }}
              value={content.filter.find((curItem) => curItem.type === "period")!.ref}
              onChange={(event) => {
                const filter = content.filter.filter((curItem) => curItem.type !== "period")!;
                filter.push({ type: "period", ref: event.currentTarget.value as Period });
                this.setState({ isChanged: true, content: { ...content, filter } });
              }}
            >
              {periodItems}
            </Form.Select>
          </>
        )}
      </>
    );
  }

  private renderChargerFilters(filterItems?: JSX.Element[]) {
    const content = this.state.content as ChargerReportContent;
    return (
      <>
        <Form.Check
          id="filterCategories"
          className="mb-2"
          checked={
            content.filter.filter((curItem) => ["category"].includes(curItem.type)).length > 0
          }
          onChange={(event) => {
            const filter = content.filter.filter((curItem) => !["category"].includes(curItem.type));
            if (event.currentTarget.checked) {
              filter.push({ type: "category", value: "" });
            }
            this.setState({
              isChanged: true,
              content: { ...content, filter: filter as IChargerReportFilter[] },
            });
          }}
          label={
            <>
              <FormattedMessage id="Category" />
              {content.filter.filter((curItem) => curItem.type === "category").length > 0 && (
                <Form.Control
                  type="text"
                  className="mb-2"
                  value={content.filter
                    .filter((curItem) => curItem.type === "category")
                    .map((curItem) => `${curItem.value}`)}
                  onChange={(event) => {
                    const filter = content.filter.filter((curItem) => curItem.type !== "category");
                    filter.push({ type: "category", value: event.target.value });
                    this.setState({ isChanged: true, content: { ...content, filter } });
                  }}
                ></Form.Control>
              )}
            </>
          }
        />
        {isSet(filterItems) && (
          <Form.Check
            id="filterRFIDUser"
            className="mb-2"
            checked={
              content.filter.filter((curItem) => ["rfiduser"].includes(curItem.type)).length > 0
            }
            onChange={(event) => {
              const filter = content.filter.filter(
                (curItem) => !["rfiduser"].includes(curItem.type),
              );
              if (event.currentTarget.checked) {
                filter.push({ type: "rfiduser", value: "" });
              }
              this.setState({ isChanged: true, content: { ...content, filter } });
            }}
            label={
              <>
                <FormattedMessage id="ChargerColumn_user" />
                {content.filter.filter((curItem) => curItem.type === "rfiduser").length > 0 && (
                  <Form.Select
                    className="mb-2"
                    multiple={true}
                    title={this.props.intl.formatMessage(messages.selectHint)}
                    value={content.filter
                      .filter((curItem) => curItem.type === "rfiduser")
                      .map((curItem) => `${curItem.value}`)}
                    onChange={(event) => {
                      const filter = content.filter.filter(
                        (curItem) => curItem.type !== "rfiduser" || curItem.value === "",
                      );
                      const rfidUserList = Array.from(
                        (event.currentTarget as unknown as HTMLSelectElement).selectedOptions,
                      ).map((item) => item.value);
                      rfidUserList.forEach((curUser) => {
                        filter.push({ type: "rfiduser", value: curUser });
                      });
                      this.setState({ isChanged: true, content: { ...content, filter } });
                    }}
                  >
                    {filterItems}
                  </Form.Select>
                )}
              </>
            }
          />
        )}
      </>
    );
  }

  private renderChargerSettings() {
    const rfidoptions = this.props.rfids
      .filter((anRfid) => anRfid.isActive)
      .sort((a, b) => {
        const keyA = a.name ? a.name : a.rfid;
        const keyB = b.name ? b.name : b.rfid;
        return isSet(keyA) && isSet(keyB) && keyA > keyB ? 1 : -1;
      })
      .map((anRfid) => {
        return (
          <option key={`${anRfid.rfid}`} value={`${anRfid.rfid}`}>
            {`${anRfid.name} [${anRfid.rfid}]`}
          </option>
        );
      });

    return (
      <>
        {this.renderDataColumns()}
        {this.renderFilter(rfidoptions)}
      </>
    );
  }

  private renderIoSettings() {
    return (
      <>
        {this.renderGrouping()}
        {this.renderFilter()}
      </>
    );
  }

  private renderSiteSettings() {
    return (
      <>
        {this.props.curUser?.role === UserRole.root && (
          <Form.Group as={Row} className="mb-3">
            <Form.Label sm={3}>
              <FormattedMessage id="Company" />
            </Form.Label>
            <Col sm={9}>
              <Form.Control
                id="company"
                placeholder="embyt"
                type="text"
                value={(this.state.content as SiteReportContent).company}
                onChange={(event) => {
                  this.setState({
                    isChanged: true,
                    content: {
                      ...this.state.content,
                      company: event?.currentTarget.value,
                    },
                  });
                }}
              />
            </Col>
          </Form.Group>
        )}
      </>
    );
  }

  private renderFileFormat() {
    const fileFormats = ["xlsx", "xls", "ods", "csv", "html"] as (keyof Report["fileFormat"])[];
    const fileformatItems = fileFormats.map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.props.intl.formatMessage({ id: `ReportFormat_${curItem}` })}
        </option>
      );
    });

    return (
      <Form.Group as={Row} className="mb-2">
        <Form.Label sm={3}>
          <FormattedMessage id="Output Format" />
        </Form.Label>
        <Col sm={9}>
          <Form.Select
            id="fileFormat"
            value={this.state.fileFormat ?? fileFormats[0]}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                fileFormat: event.currentTarget.value as keyof Report["fileFormat"],
              });
            }}
          >
            {fileformatItems}
          </Form.Select>
        </Col>
      </Form.Group>
    );
  }

  private renderCSVDelimiter() {
    const delimiters = [",", ";", "\t", "|"] as (keyof Report["csvDelimiter"])[];
    const delimiterItems = delimiters.map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.props.intl.formatMessage({ id: `ReportCSVDelimiter_${curItem}` })}
        </option>
      );
    });

    return (
      <Form.Group as={Row} className="mb-2">
        <Form.Label sm={3}>
          <FormattedMessage id="Delimiter" />
        </Form.Label>
        <Col sm={9}>
          <Form.Select
            id="csvDelimiter"
            value={this.state.csvDelimiter ?? delimiters[0]}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                csvDelimiter: event.currentTarget.value as keyof Report["csvDelimiter"],
              });
            }}
          >
            {delimiterItems}
          </Form.Select>
        </Col>
      </Form.Group>
    );
  }

  private renderHideSumRowCheckbox() {
    return (
      <Col sm={9}>
        <Form.Check
          id="hideSumRow"
          className="mt-3 mb-2"
          checked={this.state.hideSumRow ?? false}
          onChange={(event) => {
            this.setState({
              isChanged: true,
              hideSumRow: event?.currentTarget.checked,
            });
          }}
          label={<FormattedMessage id="Hide Sum Row" />}
        />
      </Col>
    );
  }

  private renderExport() {
    const periodItems = ["none", Period.DAY, Period.MONTH, Period.YEAR].map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.props.intl.formatMessage({ id: `AutoexportPeriod_${curItem}` })}
        </option>
      );
    });

    return (
      <Form.Group as={Row} className="mt-3 mb-3">
        <Form.Label sm={3}>
          <FormattedMessage id="Auto-Export" />
        </Form.Label>
        <Col sm={9}>
          <Form.Group className="mt-1">
            <Form.Label htmlFor="exportPeriod">
              <FormattedMessage id="period" />
            </Form.Label>
            :{" "}
            <Form.Select
              id="exportPeriod"
              style={{ display: "inline-block" }}
              value={this.state.autoExport.interval ?? "none"}
              onChange={(event) => {
                const newExport = {
                  interval: event.currentTarget.value as Period | "none",
                  target: this.state.autoExport.target ?? "",
                };
                this.setState({
                  isChanged: true,
                  autoExport: newExport,
                });
              }}
            >
              {periodItems}
            </Form.Select>
          </Form.Group>
          {this.state.autoExport.interval !== "none" && (
            <Form.Group className="mt-2">
              <Form.Label htmlFor="exportTarget">
                <FormattedMessage id="recipient" />
              </Form.Label>
              :{" "}
              <Form.Control
                id="exportTarget"
                style={{ display: "inline-block", width: "75%" }}
                className={this.state.autoExport.target ? undefined : "input-error"}
                placeholder={this.props.intl.formatMessage(messages.exportTargetHint)}
                type="text"
                value={this.state.autoExport.target ?? ""}
                onChange={(event) => {
                  const newExport = {
                    interval: this.state.autoExport.interval ?? "none",
                    target: event.currentTarget.value.replace(";", ","),
                  };
                  this.setState({ isChanged: true, autoExport: newExport });
                }}
              />
            </Form.Group>
          )}
        </Col>
      </Form.Group>
    );
  }

  private makeEmptyReport(reportType: Report["type"]) {
    const content =
      reportType === undefined || reportType === "trend"
        ? ({
            groupBy: ["date", "circuit"],
            aggregation: Period.MONTH,
            columns: [TrendingColum.ENERGY],
            filter: [],
            reference: undefined,
            hideEmpty: true,
          } as TrendReportContent)
        : reportType === "charger"
        ? ({
            groupBy: [],
            columns: [ChargerColum.ENERGY, ChargerColum.DURATION],
            filter: [],
          } as ChargerReportContent)
        : reportType === "io"
        ? ({
            aggregation: Period.MONTH,
            filter: [],
          } as IoReportContent)
        : ({
            filter: [],
            company: this.props.curUser?.company,
          } as SiteReportContent);

    return {
      title: this.props.intl.formatMessage(messages.titleDefault),
      isActive: true,
      type: reportType,
      content,
      autoExport: {
        target: "",
        interval: "none",
      },
    } as Report;
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(ModalReportSettingsItemComponent),
);
